import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { useIsMobile } from "../../hooks/useResponsive";
import { db } from "../../firebase";
import { tolerantId } from "../../utils/firestoreHelpers";
import { useToast } from "../../components/Toast";
import {
  collection,
  query,
  where,
  getDocs,
  updateDoc,
  doc,
  orderBy,
  Timestamp,
} from "firebase/firestore";
import {
  LuClipboardList,
  LuLoader,
  LuTriangleAlert,
  LuPlay,
  LuCircleCheck,
  LuClock,
  LuFlag,
  LuCalendarDays,
} from "react-icons/lu";

const TABS = [
  { key: "pendiente", label: "Pendientes" },
  { key: "en_progreso", label: "En Progreso" },
  { key: "completada", label: "Completadas" },
];

const TareasEmployee = ({ userid }) => {
  const isMobile = useIsMobile();
  const { selectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("pendiente");
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (selectedBuilding) {
      loadTasks();
    } else {
      setLoading(false);
    }
  }, [selectedBuilding]);

  const loadTasks = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "employee_tasks"),
        where("buildingid", "in", tolerantId(selectedBuilding.buildingid)),
        where("employeeid", "==", String(userid))
      );
      const snap = await getDocs(q);
      const data = [];
      snap.forEach((d) => data.push({ id: d.id, ...d.data() }));
      // Ordenar por dueDate en el cliente (evita requerir composite index en Firestore)
      data.sort((a, b) => {
        const da = a.dueDate?.toDate?.() || a.dueDate || 0;
        const db2 = b.dueDate?.toDate?.() || b.dueDate || 0;
        return da - db2;
      });
      setTasks(data);
    } catch (error) {
      console.error("Error loading tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    setActionLoading(taskId);
    try {
      const updateData = { status: newStatus };
      if (newStatus === "completada") {
        updateData.completedAt = Timestamp.now();
      }
      await updateDoc(doc(db, "employee_tasks", taskId), updateData);
      setTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, ...updateData } : t))
      );
    } catch (error) {
      console.error("Error updating task:", error);
      showToast("Error al actualizar la tarea.", "error");
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = tasks.filter((t) => t.status === activeTab);

  const getPriorityConfig = (priority) => {
    const map = {
      alta: { color: "#ef4444", bg: "#fee2e2", label: "Alta" },
      media: { color: "#f59e0b", bg: "#fef3c7", label: "Media" },
      baja: { color: "#059669", bg: "#d1fae5", label: "Baja" },
    };
    return map[priority] || map.media;
  };

  const formatDate = (dateVal) => {
    if (!dateVal) return "—";
    if (dateVal?.toDate) {
      return dateVal.toDate().toLocaleDateString("es-UY", {
        day: "2-digit", month: "short", year: "numeric",
      });
    }
    return new Date(dateVal).toLocaleDateString("es-UY", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    const due = dueDate?.toDate ? dueDate.toDate() : new Date(dueDate);
    return due < new Date() && activeTab !== "completada";
  };

  const getTabCount = (status) => tasks.filter((t) => t.status === status).length;

  if (!selectedBuilding) {
    return (
      <div style={styles.emptyContainer}>
        <LuTriangleAlert size={48} color="#f59e0b" />
        <h2 style={styles.emptyTitle}>Sin edificio asignado</h2>
      </div>
    );
  }

  return (
    <div style={{ ...styles.container, padding: isMobile ? 16 : 24 }}>
      {/* Header */}
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>Mis Tareas</h1>
        <p style={styles.pageSubtitle}>{selectedBuilding.name}</p>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            style={{
              ...styles.tab,
              backgroundColor: activeTab === tab.key ? "#0080ff" : "transparent",
              color: activeTab === tab.key ? "#ffffff" : "#64748b",
              boxShadow: activeTab === tab.key ? "0 2px 4px rgba(0,128,255,0.3)" : "none",
            }}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span style={{
              ...styles.tabCount,
              backgroundColor: activeTab === tab.key ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.1)",
              color: "inherit",
            }}>
              {getTabCount(tab.key)}
            </span>
          </button>
        ))}
      </div>

      {/* List */}
      {loading ? (
        <div style={styles.loadingCenter}>
          <LuLoader size={24} color="#0080ff" style={{ animation: "spin 1s linear infinite" }} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={styles.emptyState}>
          <LuClipboardList size={40} color="#d1d5db" />
          <p style={{ color: "#6b7280", fontSize: 14, marginTop: 12 }}>
            No hay tareas {activeTab === "pendiente" ? "pendientes" : activeTab === "en_progreso" ? "en progreso" : "completadas"}.
          </p>
        </div>
      ) : (
        <div style={styles.list}>
          {filtered.map((task) => {
            const priority = getPriorityConfig(task.priority);
            const overdue = isOverdue(task.dueDate);

            return (
              <div
                key={task.id}
                style={{
                  ...styles.card,
                  borderLeft: `4px solid ${priority.color}`,
                }}
              >
                <div style={styles.cardContent}>
                  <div style={styles.cardTop}>
                    <div style={styles.cardTitleRow}>
                      <h3 style={styles.cardTitle}>{task.title}</h3>
                      <span style={{
                        ...styles.priorityBadge,
                        backgroundColor: priority.bg,
                        color: priority.color,
                      }}>
                        <LuFlag size={10} />
                        {priority.label}
                      </span>
                    </div>
                    {task.description && (
                      <p style={styles.cardDesc}>{task.description}</p>
                    )}
                  </div>

                  <div style={styles.cardBottom}>
                    <div style={styles.cardMeta}>
                      <span style={{
                        ...styles.metaItem,
                        color: overdue ? "#ef4444" : "#6b7280",
                      }}>
                        <LuCalendarDays size={11} />
                        {overdue ? "Vencida: " : "Vence: "}
                        {formatDate(task.dueDate)}
                      </span>
                      {task.assignedBy && (
                        <span style={styles.metaItem}>
                          Asignada por: {task.assignedBy}
                        </span>
                      )}
                    </div>

                    <div style={styles.cardActions}>
                      {activeTab === "pendiente" && (
                        <button
                          style={styles.startBtn}
                          onClick={() => handleStatusChange(task.id, "en_progreso")}
                          disabled={actionLoading === task.id}
                        >
                          {actionLoading === task.id ? (
                            <LuLoader size={12} style={{ animation: "spin 1s linear infinite" }} />
                          ) : (
                            <LuPlay size={12} />
                          )}
                          Iniciar
                        </button>
                      )}
                      {activeTab === "en_progreso" && (
                        <button
                          style={styles.completeBtn}
                          onClick={() => handleStatusChange(task.id, "completada")}
                          disabled={actionLoading === task.id}
                        >
                          {actionLoading === task.id ? (
                            <LuLoader size={12} style={{ animation: "spin 1s linear infinite" }} />
                          ) : (
                            <LuCircleCheck size={12} />
                          )}
                          Completar
                        </button>
                      )}
                      {activeTab === "completada" && task.completedAt && (
                        <span style={styles.completedText}>
                          <LuCircleCheck size={12} color="#059669" />
                          Completada {formatDate(task.completedAt)}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    padding: 24,
    maxWidth: 900,
    margin: "0 auto",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  emptyContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "60vh",
    gap: 12,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: 400,
    color: "#111827",
  },
  pageHeader: {
    marginBottom: 20,
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "#6b7280",
    margin: "4px 0 0",
  },
  tabs: {
    display: "flex",
    gap: 4,
    marginBottom: 20,
    background: "#f3f4f6",
    borderRadius: 8,
    padding: 4,
    border: "1px solid #e5e7eb",
  },
  tab: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "8px 16px",
    borderRadius: 6,
    border: "none",
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.2s ease",
  },
  tabCount: {
    fontSize: 11,
    fontWeight: 400,
    padding: "2px 8px",
    borderRadius: 10,
  },
  loadingCenter: {
    display: "flex",
    justifyContent: "center",
    padding: 48,
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: 48,
  },
  list: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  cardContent: {
    padding: 20,
  },
  cardTop: {
    marginBottom: 12,
  },
  cardTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 400,
    color: "#111827",
    margin: 0,
  },
  priorityBadge: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    fontSize: 11,
    fontWeight: 400,
    padding: "3px 10px",
    borderRadius: 12,
    flexShrink: 0,
  },
  cardDesc: {
    fontSize: 14,
    color: "#6b7280",
    margin: "8px 0 0",
    lineHeight: 1.5,
  },
  cardBottom: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  cardMeta: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  metaItem: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#6b7280",
  },
  cardActions: {
    display: "flex",
    gap: 8,
  },
  startBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    backgroundColor: "#0080ff",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  completeBtn: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    backgroundColor: "#059669",
    color: "#ffffff",
    border: "none",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 400,
    cursor: "pointer",
    fontFamily: "'FT Habit', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  completedText: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: "#059669",
    fontWeight: 400,
  },
};

export default TareasEmployee;
